import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer } from 'react-leaflet';
import LeafletMapComponent from './LeafletMapComponent';
import { LatLngBounds, LatLngExpression } from 'leaflet';
import { MapConfig, Marker } from './MapsInterfaces';
import Keys from '../../Translations/generated/park-map.en.json.keys';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faMapMarked } from '@fortawesome/free-solid-svg-icons';
import { ApiImageContainer } from '@ibe/api';
import { useMount } from 'react-use';
import CmsGlobalElements from '../../Models/CMS/CmsGlobalElements';
import useGlobalElementsService from '../../Hooks/useGlobalElementsService';

type HistoryMethod = (data: unknown, unused: string, url?: string | URL | null) => void;

const ParkMap = ({
  mapConfig,
  mapStaticImage,
  handleHotelClick,
  displayMode,
  mapButtonLabel
}: {
  mapConfig: MapConfig;
  mapStaticImage?: ApiImageContainer;
  handleHotelClick?: (hotelId: string) => Promise<void>;
  displayMode: 'IMAGE' | 'BUTTON';
  mapButtonLabel?: string;
}): JSX.Element => {
  const mapObjectRef = useRef<
    (L.Map & { toggleFullscreen: () => void; isFullscreen: () => void }) | null
  >(null);
  const { t } = useTranslation('park-map');
  const containerRef = useRef<HTMLDivElement>(null);
  const innerMapRef = useRef<(L.Map & { toggleFullscreen: () => void }) | null>(null);

  const cmsGlobalElementsService = useGlobalElementsService();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [useNewMap, setUseNewMap] = useState(false);

  useEffect(() => {
    if (!isModalOpen) return;

    const originalReplaceState = globalThis.history.replaceState;
    const originalPushState = globalThis.history.pushState;

    const blockRogueParams = (method: HistoryMethod) =>
      function (this: History, state: unknown, unused: string, url?: string | URL | null) {
        if (url && typeof url === 'string' && url.includes('fromOnPopstate')) {
          const cleanUrl = url
            .replace('&fromOnPopstate=true', '')
            .replace('?fromOnPopstate=true', '');
          return method.call(this, state, unused, cleanUrl);
        }
        return method.call(this, state, unused, url);
      };

    globalThis.history.replaceState = blockRogueParams(originalReplaceState);
    globalThis.history.pushState = blockRogueParams(originalPushState);

    const handlePopState = (e: PopStateEvent) => {
      e.stopImmediatePropagation();
      setIsModalOpen(false);
    };

    globalThis.addEventListener('popstate', handlePopState, { capture: true });

    return () => {
      globalThis.history.replaceState = originalReplaceState;
      globalThis.history.pushState = originalPushState;
      globalThis.removeEventListener('popstate', handlePopState, { capture: true });
    };
  }, [isModalOpen]);

  useEffect(() => {
    return () => {
      if (mapObjectRef.current) {
        mapObjectRef.current.remove();
        mapObjectRef.current = null;
      }
    };
  }, []);

  useMount(() => {
    cmsGlobalElementsService.fetchGlobalElements().then((ge: CmsGlobalElements) => {
      if (ge) {
        if (ge.showNewMap) {
          setUseNewMap(ge.showNewMap);
        }
      }
    });
  });

  const isNewMapCondition = (marker: Marker) => {
    if (useNewMap) {
      return (
        marker.isPark ||
        (!marker.isPark && (marker?.distance?.floatDistance === 0 || !marker?.distance))
      );
    } else {
      return marker?.distance?.floatDistance === 0 || !marker?.distance;
    }
  };

  const bounds: LatLngBounds = useMemo(() => {
    const filteredMarkers: LatLngExpression[] = (mapConfig.markers || [])
      .filter(marker => !!marker.geoData && isNewMapCondition(marker))
      .map(marker => [marker?.geoData?.latitude || 0, marker?.geoData?.longitude || 0]);

    const latLngBounds = new LatLngBounds(filteredMarkers);

    if (mapConfig.pad) {
      return latLngBounds.pad(mapConfig.pad);
    }

    return latLngBounds;
  }, [useNewMap, mapConfig.markers]);

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? 'hidden' : 'unset';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const handleOpenModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isModalOpen) return;

    const currentState = globalThis.history.state || {};
    globalThis.history.pushState(
      { ...currentState, mapModalOpen: true },
      '',
      globalThis.location.href
    );
    setIsModalOpen(true);
  };

  const handleCloseModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isModalOpen) return;

    if (globalThis.history.state?.mapModalOpen) {
      globalThis.history.back();
    } else {
      setIsModalOpen(false);
    }
  };

  return (
    <>
      {isModalOpen ? (
        <div className="map-modal" ref={containerRef}>
          <button className="close-button" onClick={handleCloseModal}>
            {t(Keys.close)} <FontAwesomeIcon icon={faClose} />
          </button>

          <MapContainer
            key={mapConfig.mapKey}
            ref={mapObjectRef}
            bounds={bounds.isValid() ? bounds : undefined}
          >
            <LeafletMapComponent
              markers={mapConfig.markers}
              bounds={bounds}
              centerMap={mapConfig.centerMap}
              containerRef={containerRef}
              innerMapRef={innerMapRef}
              handleHotelClick={handleHotelClick}
            />
          </MapContainer>
        </div>
      ) : (
        <>
          {displayMode === 'IMAGE' && (
            <button className={'map-preview-wrapper'} onClick={handleOpenModal}>
              {mapStaticImage?.small && (
                <img src={mapStaticImage?.small.url} alt={mapStaticImage?.small.altText} />
              )}
              <div className={'map-preview-banner'}>
                <a>{t(Keys.bannerText)}</a>
              </div>
            </button>
          )}
          {displayMode === 'BUTTON' && (
            <button className={'map-button'} onClick={handleOpenModal}>
              <FontAwesomeIcon icon={faMapMarked} />
              {mapButtonLabel || t(Keys.MAP)}
            </button>
          )}
        </>
      )}
    </>
  );
};

export default ParkMap;
